import { Dispatch, MutableRef, StateUpdater, useEffect, useRef, useState } from "preact/hooks";
import { FortuneWheelApi, FortuneWheelResponse, PrizeEntry } from "../../../../services/bonus/FortuneWheel";
import User from "../../../../services/user";
import { CloseIcon } from "../../utils/icons";
import { MarketingEvents } from "../../../../utils/gtm-module";

export function FortuneWheel() {
    const wheel = useFortuneWheel();
    if (wheel == null)
        return null;
    const {wheelController, fortuneWheelApi, setWheelSettings, wheelFrame} = wheel;
    return <WheelTemplate onSpinClick={e => {
        new SpinProcedure(wheelController, fortuneWheelApi).spin().then(e => {
            fortuneWheelApi.getFortuneWheelStatus().then(setWheelSettings);
        });
    } } onRef={wheelFrame} onClose={() => {
        setWheelSettings(null);
    } } />;
}

export type WheelTemplateParams = {
    onSpinClick: (e: Event) => void, 
    onClose: (e: Event) => void, 
    onRef: MutableRef<HTMLDivElement>
};

export const WheelTemplate = ({onSpinClick, onRef, onClose}: WheelTemplateParams) => <div class="fixed bottom-0 left-0 right-0 min-h-[450px] h-[60vh] max-h-screen">
    <div class="bg-slate-800/80 h-full w-full rounded-t-lg text-white pt-2">
        <div class="py-2 text-lg uppercase font-bold text-center">WHEEL OF FORTUNE</div>
        <div class="aspect-square w-[320px] mx-auto relative" ref={onRef}>
            {/* Qui va la ruota */}
            <div class="absolute w-full top-0 z-10 flex justify-center">
                <img class="relative h-8 -top-2" src={require("./assets/tip.png")} />
            </div>
        </div>
        <div class="flex items-center justify-center pt-2">
            <button class="bg-green-600 font-bold text-lg text-white p-3 rounded-full w-32" onClick={onSpinClick}>SPIN</button>
        </div>
    </div>
    <img class="absolute w-24 bottom-0 left-0" src={require("./assets/decoration.png")} />
    <img class="absolute w-24 bottom-0 right-0 -scale-x-100" src={require("./assets/decoration.png")} />
    <button onClick={onClose} class="absolute text-lg text-white right-0 top-0 p-4">
        <CloseIcon class="h-6 fill-current" />
    </button>
</div>

export type WheelComponentParams = {
    wheelController: WheelController, 
    fortuneWheelApi: FortuneWheelApi, 
    setWheelSettings: Dispatch<StateUpdater<FortuneWheelResponse>>, 
    wheelFrame: MutableRef<HTMLDivElement>
}

export const useFortuneWheel = (): WheelComponentParams | null => {
    const [fortuneWheelApi] = useState(() => new FortuneWheelApi(window.safeLocalStorage));
    const [wheelSettings, setWheelSettings] = useState<FortuneWheelResponse | null>(null);

    // On mount
    useEffect(() => {
        fortuneWheelApi.getFortuneWheelStatus().then(setWheelSettings)
    }, []);
    // On deposits
    useEffect(() => {
        const reloadWheelInfo = () => {
            fortuneWheelApi.getFortuneWheelStatus().then(setWheelSettings)
        }
        window.addEventListener(MarketingEvents.DEPOSIT_SUCCESS, reloadWheelInfo)
        return () => window.removeEventListener(MarketingEvents.DEPOSIT_SUCCESS, reloadWheelInfo)
    }, []);

    const enabled = wheelSettings?.enabled || false;
    const prizes = wheelSettings?.prizes || [];

    if (!enabled || prizes.length == 0)
        return null;

    const [templateBuilder] = useState(() => new WheelTemplateBuilder(prizes));
    const [wheelDom] = useState(() => templateBuilder.build());
    const [wheelController] = useState(() => new WheelController(prizes, wheelDom));

    const wheelFrame: MutableRef<HTMLDivElement> = useRef();
    useEffect(() => {
        if (wheelFrame.current != null) {
            wheelFrame.current.appendChild(wheelDom.getDom());
        }
    }, [wheelFrame.current]);

    return {wheelController, fortuneWheelApi, setWheelSettings, wheelFrame};
}

export class SpinProcedure {
    constructor(
        private readonly wheelController: WheelController,
        private readonly fortuneWheelApi: FortuneWheelApi
    ) {
    }

    public async spin() {
        // avvia ruota (loop)
        this.wheelController.start();
        // Lancia chiamata HTTP
        const apiRequest = this.fortuneWheelApi.claimFortuneWheel();
        // Avvia timer
        const timer = new Promise((res) => setTimeout(res, 1.3e3));
        // await http + timer
        await Promise.all([apiRequest, timer]);
        // leggi response
        const response = await apiRequest;
        // Segnala alla ruota di fermarsi sullo spicchio N
        const winIdx = response.id;
        this.wheelController.stopOnIdx(winIdx);
        new Animator(
            0.5 * 2, 
            t => this.wheelController.animation.setTimeScale((1 - t) * 0.8 + 0.2),
            () => {},
            () => {},
            1
        ).start();
        // Aspetta 5 secondi
        await new Promise((res) => setTimeout(res, 5e3));
        // Esci e mostra il bonus
        if (response.prize)
            User.forceBonusNotification(parseFloat(response.prize!.amount));
    }
}

class WheelTemplateBuilder {
    constructor(
        private readonly prizes: PrizeEntry[]
    ) {}

    private arcTemplate = (prize: PrizeEntry, idx: number) => `<div 
        class="absolute h-1/2 w-[50px] pt-6 origin-bottom top-0 left-[135px] leading-none" 
        style="transform: rotate(${360*idx/this.prizes.length}deg)"
    >
        <div class="text-white font-bold text-center">${prize.text ?
            `<div class="pt-3">${prize.text}</div>` : 
            `<div class="text-2xl">${prize.prize.amount}</div><div>${prize.prize.currency}</div>`
        }</div>
    </div>`

    public build(): WheelRotationRoot {
        const templateNode = document.createElement('div');
        templateNode.innerHTML = `<div id="wheel-rotation-outer" class="relative will-change-transform h-[320px] w-[320px]">
            <div id="wheel-inner" class="relative will-change-transform h-full w-full">
                <img class="h-full w-full" src="${require('./assets/wheel.png')}" />
                ${this.prizes.map((prize, idx) => this.arcTemplate(prize,idx)).join('')}
            </div>
        </div>`;
        const outer = templateNode.firstElementChild as HTMLDivElement;
        const inner = templateNode.firstElementChild.firstElementChild as HTMLDivElement
        return new WheelRotationRoot(outer, inner, this.prizes.length);
    }
}

class WheelController {
    public animation?: Animator;

    constructor(
        private readonly prizes: PrizeEntry[],
        private readonly rotationRoot: WheelRotationRoot
    ) {}

    public start() {
        if (this.animation) 
            return;
        this.animation = new Animator(
            0.05,
            t => this.rotationRoot.setPosition(t),
            () => {
                this.rotationRoot.advanceArc();
                this.rotationRoot.setPosition(0);
            },
            () => this.rotationRoot.advanceArc()
        );
        this.animation.start();
    }

    public stopOnIdx(priceIdx: number) {
        const loopsRemaining = this.numArcsUpTo(priceIdx);
        this.animation.setLoops(loopsRemaining);
    }

    public numArcsUpTo(prizeIdx: number): number {
        const arcIdx = this.prizes.length - prizeIdx;
        return (arcIdx + this.prizes.length - this.rotationRoot.currentArcIdx()) % this.prizes.length + this.prizes.length;
    }
}

class WheelRotationRoot {
    private arcIdx = 0;
    private percentage = 0;
    constructor(
        private readonly outer: HTMLDivElement,
        private readonly inner: HTMLDivElement,
        private readonly numArcs: number,
    ) {}
    public getDom() {
        return this.outer;
    }
    public setPosition(percentage: number) {
        this.percentage = percentage;
        this.inner.style.transform = `rotate(${(360/this.numArcs)*percentage}deg)`
    }
    public advanceArc(): void {
        this.arcIdx = (this.arcIdx + 1) % this.numArcs;
        this.outer.style.transform = `rotate(${360*(this.arcIdx/this.numArcs)}deg)`
        this.percentage -= 1;
        this.setPosition(this.percentage)
    }
    public currentArcIdx(): number {
        return this.arcIdx;
    }
}

class Animator {
    private cursor = 0;
    private lastTs?: DOMHighResTimeStamp = undefined;
    private timeScale = 1;

    constructor(
        private readonly durationSecs: number,
        private readonly onUpdate: (t: number) => void,
        private readonly onComplete: () => void,
        private readonly onRepeat?: () => void,
        private loops: number = Infinity
    ) {}

    update: FrameRequestCallback = (now: DOMHighResTimeStamp) => {
        if (this.lastTs === undefined)
            this.lastTs = now;
        const deltaMillis = now - this.lastTs;
        this.lastTs = now;
        // logic
        this.cursor += deltaMillis / 1000 / this.durationSecs * this.timeScale;
        this.onUpdate(this.cursor);
        if (this.cursor >= 1) {
            this.restart();
            return;
        }
        // end
        requestAnimationFrame(this.update);
    }

    start() {
        requestAnimationFrame(this.update);
    }

    restart() {
        this.cursor -= 1;
        this.loops--;
        if (this.loops <= 0) {
            this.onUpdate(1);
            this.onComplete();
            return;
        }
        if (this.onRepeat)
            this.onRepeat();
        this.start();
    }

    setLoops(loops: number) {
        this.loops = loops;
    }

    setTimeScale(n: number) {
        console.log(n);
        this.timeScale = n;
    }
}
